import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  TrendingUp,
  Clock,
  Target,
  Activity,
  Calendar,
  Award,
  BookOpen,
  Brain
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { calculateLearningStatistics } from '../../../services/studentEnhancementsService';
import { useTenantContext } from '../../../context/TenantContext';

interface LearningStatsChartProps {
  studentId: string;
  courseId?: string;
  enrollmentId?: string;
  dateRange?: 'week' | 'month' | 'quarter' | 'year';
}

interface LearningStats {
  totalTimeMinutes: number;
  totalSessions: number;
  averageSessionTime: number;
  totalActions: number;
  averageProgressGain: number;
  dailyStats: Array<{
    date: string;
    timeMinutes: number;
    sessions: number;
    actions: number;
  }>;
  recentSessions: any[];
}

const LearningStatsChart: React.FC<LearningStatsChartProps> = ({
  studentId,
  courseId,
  enrollmentId,
  dateRange = 'month'
}) => {
  const { tenant } = useTenantContext();
  const [stats, setStats] = useState<LearningStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadLearningStats();
  }, [studentId, courseId, dateRange, tenant?.id]);

  const loadLearningStats = async () => {
    if (!studentId || !tenant?.id) return;

    setIsLoading(true);
    try {
      // Calculer les dates selon la période sélectionnée
      const now = new Date();
      const dateFrom = new Date();
      
      switch (dateRange) {
        case 'week':
          dateFrom.setDate(now.getDate() - 7);
          break;
        case 'month':
          dateFrom.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          dateFrom.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          dateFrom.setFullYear(now.getFullYear() - 1);
          break;
      }

      const learningStats = await calculateLearningStatistics(
        studentId,
        courseId,
        tenant.id,
        dateFrom.toISOString(),
        now.toISOString()
      );

      setStats(learningStats);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h${remainingMinutes}min` : `${hours}h`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getDateRangeLabel = (): string => {
    switch (dateRange) {
      case 'week': return '7 derniers jours';
      case 'month': return '30 derniers jours';
      case 'quarter': return '3 derniers mois';
      case 'year': return '12 derniers mois';
      default: return '30 derniers jours';
    }
  };

  // Couleurs pour les graphiques
  const colors = {
    primary: '#ef4444',
    secondary: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    info: '#8b5cf6'
  };

  // Données pour le graphique en secteurs du temps d'étude
  const timeDistributionData = stats?.dailyStats.map(day => ({
    name: formatDate(day.date),
    value: day.timeMinutes,
    sessions: day.sessions
  })) || [];

  // Données pour le graphique de progression
  const progressData = stats?.dailyStats.map(day => ({
    date: formatDate(day.date),
    timeMinutes: day.timeMinutes,
    sessions: day.sessions,
    actions: day.actions
  })) || [];

  if (isLoading) {
    return (
      <Card className="bg-black/20 border-white/10">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-white/10 rounded w-1/4"></div>
            <div className="h-32 bg-white/10 rounded"></div>
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-16 bg-white/10 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card className="bg-black/20 border-white/10">
        <CardContent className="p-6 text-center">
          <BookOpen className="mx-auto mb-4 text-white/50" size={48} />
          <p className="text-white/70">Aucune donnée d'apprentissage disponible</p>
          <p className="text-sm text-white/50">Commencez à étudier pour voir vos statistiques</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-black/20 border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center">
            <TrendingUp className="mr-2 text-red-400" size={24} />
            Statistiques d'apprentissage
          </CardTitle>
          <Badge variant="outline" className="border-white/20 text-white/70">
            {getDateRangeLabel()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-white/5">
            <TabsTrigger value="overview" className="text-white bg-yellow-800">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="progress" className="text-white bg-black/70">Progression</TabsTrigger>
            <TabsTrigger value="activity" className="text-white bg-yellow-800">Activité</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Métriques principales */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-red-900/20 to-red-800/10 p-4 rounded-lg border border-red-500/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/70">Temps total</p>
                    <p className="text-2xl font-bold text-white">
                      {formatTime(stats.totalTimeMinutes)}
                    </p>
                  </div>
                  <Clock className="text-red-400" size={24} />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-blue-900/20 to-blue-800/10 p-4 rounded-lg border border-blue-500/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/70">Sessions</p>
                    <p className="text-2xl font-bold text-white">{stats.totalSessions}</p>
                  </div>
                  <Activity className="text-blue-400" size={24} />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-green-900/20 to-green-800/10 p-4 rounded-lg border border-green-500/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/70">Moy. session</p>
                    <p className="text-2xl font-bold text-white">
                      {formatTime(stats.averageSessionTime)}
                    </p>
                  </div>
                  <Target className="text-green-400" size={24} />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-purple-900/20 to-purple-800/10 p-4 rounded-lg border border-purple-500/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/70">Interactions</p>
                    <p className="text-2xl font-bold text-white">{stats.totalActions}</p>
                  </div>
                  <Brain className="text-purple-400" size={24} />
                </div>
              </motion.div>
            </div>

            {/* Graphique de temps d'étude quotidien */}
            <div className="bg-white/5 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4">Temps d'étude quotidien</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={progressData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="rgba(255,255,255,0.7)"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="rgba(255,255,255,0.7)"
                    fontSize={12}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px',
                      color: 'white'
                    }}
                    formatter={(value: any, name: string) => [
                      name === 'timeMinutes' ? formatTime(value) : value,
                      name === 'timeMinutes' ? 'Temps d\'étude' : 
                      name === 'sessions' ? 'Sessions' : 'Interactions'
                    ]}
                  />
                  <Bar 
                    dataKey="timeMinutes" 
                    fill={colors.primary}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="progress" className="space-y-6">
            {/* Graphique de progression dans le temps */}
            <div className="bg-white/5 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4">Évolution de l'activité</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={progressData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="rgba(255,255,255,0.7)"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="rgba(255,255,255,0.7)"
                    fontSize={12}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px',
                      color: 'white'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="timeMinutes" 
                    stroke={colors.primary}
                    strokeWidth={3}
                    dot={{ fill: colors.primary, strokeWidth: 2, r: 4 }}
                    name="Temps (min)"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="sessions" 
                    stroke={colors.secondary}
                    strokeWidth={2}
                    dot={{ fill: colors.secondary, strokeWidth: 2, r: 3 }}
                    name="Sessions"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Indicateurs de progression */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white/5 p-4 rounded-lg">
                <h4 className="text-white font-semibold mb-2">Progression moyenne</h4>
                <div className="flex items-center">
                  <div className="text-3xl font-bold text-green-400">
                    +{stats.averageProgressGain}%
                  </div>
                  <div className="ml-2 text-sm text-white/70">par session</div>
                </div>
              </div>

              <div className="bg-white/5 p-4 rounded-lg">
                <h4 className="text-white font-semibold mb-2">Régularité</h4>
                <div className="flex items-center">
                  <div className="text-3xl font-bold text-blue-400">
                    {Math.round((stats.dailyStats.filter(d => d.sessions > 0).length / stats.dailyStats.length) * 100)}%
                  </div>
                  <div className="ml-2 text-sm text-white/70">de jours actifs</div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="space-y-6">
            {/* Sessions récentes */}
            <div className="bg-white/5 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4">Sessions récentes</h3>
              <div className="space-y-3">
                {stats.recentSessions.slice(0, 5).map((session, index) => (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      <div>
                        <p className="text-white text-sm">
                          {formatDate(session.created_at)}
                        </p>
                        <p className="text-white/60 text-xs">
                          {session.lesson_id ? 'Leçon' : 'Cours général'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm font-semibold">
                        {formatTime(session.time_spent_minutes)}
                      </p>
                      <p className="text-white/60 text-xs">
                        +{session.progress_after - session.progress_before}% progression
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Graphique d'activité par type */}
            <div className="bg-white/5 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4">Répartition des interactions</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={progressData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="rgba(255,255,255,0.7)"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="rgba(255,255,255,0.7)"
                    fontSize={12}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px',
                      color: 'white'
                    }}
                  />
                  <Bar 
                    dataKey="actions" 
                    fill={colors.info}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default LearningStatsChart;
